import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

function ItemCard({ title, meta, open, onToggle, onRemove, children, testId }) {
  return (
    <div className={cn("rounded-xl border bg-background/40 transition-colors", open ? "border-primary/40" : "border-border hover:border-primary/30")} data-testid={testId}>
      <div className="flex items-center gap-2 px-4 py-3">
        <button type="button" onClick={onToggle} className="min-w-0 flex-1 text-left" data-testid={`${testId}-toggle`}>
          <div className="truncate text-sm font-semibold">{title}</div>
          {meta && <div className="mt-0.5 truncate text-xs text-muted-foreground">{meta}</div>}
        </button>
        <Button type="button" variant="ghost" size="sm" onClick={onToggle} className="h-8 gap-1.5 text-xs" data-testid={`${testId}-edit`}>
          {open ? <><Check className="h-3.5 w-3.5" />Done</> : <><Pencil className="h-3.5 w-3.5" />Edit</>}
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} aria-label="Remove" className="h-8 w-8 text-muted-foreground hover:text-rose-400" data-testid={`${testId}-remove`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {open && <div className="space-y-4 border-t border-border px-4 py-4 reveal-up">{children}</div>}
    </div>
  );
}

export function ListEditor({ items, onChange, blank, addLabel, itemTitle, itemMeta, renderFields, emptyText, testId }) {
  const [openIdx, setOpenIdx] = useState(items.length === 1 ? 0 : null);
  const setItem = (i) => (patch) => onChange((list) => list.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const remove = (i) => {
    const removed = items[i];
    onChange((list) => list.filter((_, idx) => idx !== i));
    setOpenIdx(null);
    toast("Removed", { description: itemTitle(removed), action: { label: "Undo", onClick: () => onChange((list) => { const n = [...list]; n.splice(i, 0, removed); return n; }) } });
  };
  const add = () => { onChange((list) => [...list, { ...blank }]); setOpenIdx(items.length); };
  return (
    <div className="space-y-2.5" data-testid={testId}>
      {items.length === 0 && <p className="text-sm text-muted-foreground">{emptyText}</p>}
      {items.map((it, i) => (
        <ItemCard key={i} testId={`${testId}-item-${i}`} title={itemTitle(it)} meta={itemMeta(it)} open={openIdx === i}
          onToggle={() => setOpenIdx(openIdx === i ? null : i)} onRemove={() => remove(i)}>
          {renderFields(it, setItem(i), i)}
        </ItemCard>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1.5 border-dashed" data-testid={`${testId}-add`}>
        <Plus className="h-3.5 w-3.5" />{addLabel}
      </Button>
    </div>
  );
}
